import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { config } from '../config/environment';

export const CallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setIsLoggedIn, setUser } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        console.log('[CallbackPage] Callback received:', {
          hasCode: !!code,
          hasState: !!state,
          error,
          errorDescription
        });

        // Google OAuth 에러 처리
        if (error) {
          console.error('[CallbackPage] Google OAuth error:', error, errorDescription);
          alert(`로그인 실패: ${error}`);
          navigate('/');
          return;
        }

        // State 검증 (CSRF 방지)
        const savedState = sessionStorage.getItem('google_oauth_state');
        if (state !== savedState) {
          console.error('[CallbackPage] State mismatch - possible CSRF attack');
          alert('보안 검증 실패. 다시 로그인해주세요.');
          sessionStorage.removeItem('google_oauth_state');
          navigate('/');
          return;
        }
        sessionStorage.removeItem('google_oauth_state');

        // Authorization Code Flow: 코드를 백엔드로 전송
        if (code) {
          console.log('[CallbackPage] Exchanging authorization code with backend...');
          
          try {
            const response = await axios.post(
              `${config.apiUrl}/api/auth/google/callback`,
              { code },
              { headers: { 'Content-Type': 'application/json' } }
            );

            console.log('[CallbackPage] Backend response:', response.status);

            if (response.data.success) {
              const token = response.data.token;
              const user = response.data.user;

              if (!token || !user) {
                throw new Error('Invalid response: missing token or user data');
              }

              console.log('[CallbackPage] Token exchanged successfully for user:', user.email);

              // 토큰 및 사용자 정보 저장
              localStorage.setItem('authToken', token);
              localStorage.setItem('user', JSON.stringify({
                id: user.id,
                email: user.email,
                name: user.name,
                provider: 'google'
              }));

              // AuthContext 업데이트
              setIsLoggedIn(true);
              setUser({
                id: user.id,
                email: user.email,
                name: user.name,
                provider: 'google'
              });

              console.log('[CallbackPage] ✅ User logged in:', user.email);

              // 대시보드로 리다이렉트
              navigate('/');
            } else {
              throw new Error(response.data.error || 'Login failed');
            }
          } catch (axiosError: any) {
            console.error('[CallbackPage] Backend error:', {
              status: axiosError.response?.status,
              error: axiosError.response?.data?.error,
              message: axiosError.message
            });
            alert(`로그인 실패: ${axiosError.response?.data?.error || axiosError.message}`);
            navigate('/');
          }
          return;
        }

        // 대체: 이전 방식 (token/user 쿼리 파라미터) - 하위호환성 유지
        const token = searchParams.get('token');
        const userStr = searchParams.get('user');

        if (token && userStr) {
          console.log('[CallbackPage] Using legacy callback method (token + user params)');
          const user = JSON.parse(decodeURIComponent(userStr));
          
          // 토큰 저장
          localStorage.setItem('authToken', token);
          localStorage.setItem('user', JSON.stringify(user));
          
          // AuthContext 업데이트
          setIsLoggedIn(true);
          setUser(user);
          
          console.log('[CallbackPage] ✅ User logged in (legacy):', user.email);
          navigate('/');
          return;
        }

        console.error('[CallbackPage] Missing required parameters');
        alert('로그인 실패: 필수 정보가 없습니다.');
        navigate('/');
      } catch (error) {
        console.error('[CallbackPage] Error processing callback:', error);
        alert('로그인 처리 중 오류가 발생했습니다.');
        navigate('/');
      }
    };

    handleCallback();
  }, [searchParams, navigate, setIsLoggedIn, setUser]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">로그인 중...</h1>
        <p className="text-gray-600">잠시만 기다려주세요.</p>
      </div>
    </div>
  );
};
